import { useEffect, type ReactNode } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";

import {
  ClientButton,
  ClientInput,
  ClientSlider,
  ClientStepper,
  type CalculatorStepId,
} from "@/components/clients";
import { clientPath, useClientBasePath } from "@/hooks/use-client-base-path";
import { useTciSession } from "@/hooks/use-tci-session";
import { useVisitorTracking } from "@/hooks/use-visitor-tracking";
import {
  getCurrencySymbol,
  getQuestionsForStep,
  hasSectorFrictionQuestions,
  isWorkflowId,
  parseNumericInput,
} from "@/lib/tci";
import { sessionInputsToTrackingPayload } from "@/lib/visitor-tracking-payload";
import type { TciSession } from "@/providers/tci-session-context";

/** Exact content width from Figma frames 370:421 / 374:1576 / 374:1625. */
const CALCULATOR_WIDTH_PX = 539;

function parseStep(raw: string | undefined): CalculatorStepId | null {
  const n = Number(raw);
  if (n === 1 || n === 2 || n === 3) return n;
  return null;
}

function isFilledNumeric(value: string): boolean {
  return Number.isFinite(parseNumericInput(value));
}

function isStepComplete(session: TciSession, step: CalculatorStepId): boolean {
  if (step === 1) {
    return (
      isFilledNumeric(session.labor.headcount) &&
      isFilledNumeric(session.labor.salary)
    );
  }
  if (step === 2) {
    return (
      isFilledNumeric(session.yieldPenalty.volume) &&
      isFilledNumeric(session.yieldPenalty.penaltyPerEvent)
    );
  }
  return (
    isFilledNumeric(session.untapped.volume) &&
    isFilledNumeric(session.untapped.valuePerAction)
  );
}

function QuestionField({
  number,
  label,
  children,
  labelClassName,
}: {
  number: number;
  label: string;
  children: ReactNode;
  labelClassName?: string;
}) {
  return (
    <div className="flex w-full flex-col gap-2">
      <p
        className={
          labelClassName ??
          "font-['Inter'] text-base font-normal leading-9 tracking-[-1.2px] text-[#f5f3ee] sm:text-[20px]"
        }
      >
        <span className="tabular-nums">{number}. </span>
        {label}
      </p>
      {children}
    </div>
  );
}

function CalculatorNav({
  onBack,
  onContinue,
  canContinue,
}: {
  onBack: () => void;
  onContinue: () => void;
  canContinue: boolean;
}) {
  return (
    <div className="flex h-auto w-full flex-col gap-2 sm:h-[46px] sm:flex-row sm:items-center sm:gap-2">
      <ClientButton variant="secondary" type="button" onClick={onBack}>
        Back
      </ClientButton>
      <div className="flex min-w-0 flex-1 sm:justify-end">
        <ClientButton
          variant="primary"
          type="button"
          disabled={!canContinue}
          onClick={onContinue}
        >
          Continue
        </ClientButton>
      </div>
    </div>
  );
}

/**
 * Three-step TCI calculator (Figma 370:421, 374:1576, 374:1625).
 * Labels and defaults come from the TCI engine + session store.
 */
export function ClientCalculatorPage() {
  const { step: stepParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const basePath = useClientBasePath();
  const { session, startSession, updateSession } = useTciSession();
  const { track } = useVisitorTracking();

  const step = parseStep(stepParam);
  const workflowParam = searchParams.get("workflow") ?? "";
  const workflowId = isWorkflowId(workflowParam) ? workflowParam : null;

  useEffect(() => {
    if (!workflowId) return;
    if (!session || session.workflowId !== workflowId) {
      startSession(workflowId);
    }
  }, [workflowId, session, startSession]);

  useEffect(() => {
    if (!step || !workflowId) return;
    const currentStep =
      step === 1
        ? "calculator_1"
        : step === 2
          ? "calculator_2"
          : "calculator_3";
    track({
      currentStep,
      lastEvent: `view_${currentStep}`,
      workflowId,
    });
  }, [step, workflowId, track]);

  if (!step || !workflowId) {
    return <Navigate to={basePath} replace />;
  }

  if (!session || session.workflowId !== workflowId) {
    return null;
  }

  const businessSector = session.businessSector;
  if (
    !businessSector ||
    !hasSectorFrictionQuestions(businessSector, workflowId)
  ) {
    return <Navigate to={basePath} replace />;
  }

  const activeSession = session;
  const currentStep = step;
  const regionId = activeSession.regionId;
  const currencySymbol = getCurrencySymbol(regionId);
  const workflowQuery = `?workflow=${encodeURIComponent(workflowId)}`;
  const questions = getQuestionsForStep(
    businessSector,
    workflowId,
    currentStep,
    regionId,
  );
  const q0 = questions[0]?.label ?? "";
  const q1 = questions[1]?.label ?? "";
  const q2 = questions[2]?.label ?? "";
  const canContinue = isStepComplete(activeSession, currentStep);

  function goToStep(next: CalculatorStepId) {
    navigate(`${clientPath(basePath, `calculator/${next}`)}${workflowQuery}`);
  }

  function handleBack() {
    if (currentStep === 1) {
      navigate(basePath);
      return;
    }
    goToStep((currentStep - 1) as CalculatorStepId);
  }

  function handleContinue() {
    if (!isStepComplete(activeSession, currentStep)) return;
    const funnelStep =
      currentStep === 1
        ? "calculator_1"
        : currentStep === 2
          ? "calculator_2"
          : "calculator_3";
    track({
      currentStep: funnelStep,
      lastEvent: `${funnelStep}_continue`,
      ...sessionInputsToTrackingPayload(activeSession),
    });

    if (currentStep === 3) {
      navigate(`${clientPath(basePath, "calculator/report")}${workflowQuery}`);
      return;
    }
    goToStep((currentStep + 1) as CalculatorStepId);
  }

  return (
    <section
      className="mx-auto my-auto flex w-full flex-col items-start gap-[50px]"
      style={{ maxWidth: CALCULATOR_WIDTH_PX }}
    >
      <ClientStepper currentStep={currentStep} regionId={regionId} />

      <div className="flex w-full flex-col items-center gap-10">
        {currentStep === 1 ? (
          <div className="flex w-full flex-col gap-7">
            <QuestionField number={1} label={q0}>
              <ClientInput
                variant="number"
                value={activeSession.labor.headcount}
                onChange={(e) =>
                  updateSession({ labor: { headcount: e.target.value } })
                }
                aria-label={q0}
              />
            </QuestionField>

            <QuestionField
              number={2}
              label={q1}
              labelClassName="max-w-[453px] font-['Inter'] text-base font-normal leading-7 tracking-[-1.2px] text-[#f5f3ee] sm:text-[20px]"
            >
              <ClientSlider
                value={activeSession.labor.timePercent}
                onValueChange={(timePercent) =>
                  updateSession({ labor: { timePercent } })
                }
                aria-label={q1}
              />
            </QuestionField>

            <QuestionField number={3} label={q2}>
              <ClientInput
                variant="currency"
                currencySymbol={currencySymbol}
                placeholder=""
                value={activeSession.labor.salary}
                onChange={(e) =>
                  updateSession({ labor: { salary: e.target.value } })
                }
                aria-label={q2}
              />
            </QuestionField>
          </div>
        ) : null}

        {currentStep === 2 ? (
          <div className="flex w-full flex-col gap-7">
            <QuestionField number={1} label={q0}>
              <ClientInput
                variant="number"
                value={activeSession.yieldPenalty.volume}
                onChange={(e) =>
                  updateSession({ yieldPenalty: { volume: e.target.value } })
                }
                aria-label={q0}
              />
            </QuestionField>

            <QuestionField number={2} label={q1}>
              <ClientInput
                variant="currency"
                currencySymbol={currencySymbol}
                placeholder=""
                value={activeSession.yieldPenalty.penaltyPerEvent}
                onChange={(e) =>
                  updateSession({
                    yieldPenalty: { penaltyPerEvent: e.target.value },
                  })
                }
                aria-label={q1}
              />
            </QuestionField>
          </div>
        ) : null}

        {currentStep === 3 ? (
          <div className="flex w-full flex-col gap-7">
            <QuestionField number={1} label={q0}>
              <ClientInput
                variant="number"
                value={activeSession.untapped.volume}
                onChange={(e) =>
                  updateSession({ untapped: { volume: e.target.value } })
                }
                aria-label={q0}
              />
            </QuestionField>

            <div className="flex w-full flex-col gap-4">
              <p className="max-w-[453px] font-['Inter'] text-base font-normal leading-7 tracking-[-1.2px] text-[#f5f3ee] sm:text-[20px]">
                <span className="tabular-nums">2. </span>
                {q1}
              </p>
              <ClientSlider
                value={activeSession.untapped.actionablePercent}
                onValueChange={(actionablePercent) =>
                  updateSession({ untapped: { actionablePercent } })
                }
                aria-label={q1}
              />
            </div>

            <QuestionField number={3} label={q2}>
              <ClientInput
                variant="currency"
                currencySymbol={currencySymbol}
                placeholder=""
                value={activeSession.untapped.valuePerAction}
                onChange={(e) =>
                  updateSession({
                    untapped: { valuePerAction: e.target.value },
                  })
                }
                aria-label={q2}
              />
            </QuestionField>
          </div>
        ) : null}

        <CalculatorNav
          onBack={handleBack}
          onContinue={handleContinue}
          canContinue={canContinue}
        />
      </div>
    </section>
  );
}
